import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-hot-toast';
import { useSite } from '../../context/SiteContext';
import { getBusinessDateISO } from '../../utils/businessDate';
import { addNovolexLogo } from '../../utils/pdfBranding';

const formatNumber = (value) => Number(value || 0).toLocaleString('en-US');
const formatOptional = (value) => (value == null ? '—' : formatNumber(value));
const formatHours = (minutes) => (minutes == null ? '—' : (Number(minutes) / 60).toFixed(2));
const codeLabel = (event) => [event.code, event.description].filter(Boolean).join(' - ') || '—';

const appendLaborAxReport = (doc, report) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 36;
  const machines = report.machines || [];
  const totals = report.totals || {};

  doc.setProperties({
    title: `Mano de Obra AX ${report.date} ${report.shift?.code || ''}`,
    subject: 'Renglones A/X por máquina en el orden original de captura',
  });
  addNovolexLogo(doc, { x: margin, y: 14, width: 82 });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(30, 64, 175);
  doc.text('Mano de Obra AX', margin + 100, 42);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(51, 65, 85);
  doc.text(`${report.site?.code || ''}${report.site?.name ? ` - ${report.site.name}` : ''}`, margin, 65);
  doc.text(`Fecha: ${report.date}   Turno: ${report.shift?.code || ''}${report.shift?.name ? ` - ${report.shift.name}` : ''}`, margin, 81);
  doc.text(report.include_open ? 'Incluye reportes abiertos y cerrados' : 'Solo reportes cerrados', margin, 97);
  doc.setFont('helvetica', 'bold');
  doc.text(
    `${formatNumber(totals.events)} eventos  |  ${formatNumber(totals.minutes)} min  |  Cantidad ${formatNumber(totals.quantity)}  |  Scrap ${formatNumber(totals.scrap)}`,
    pageWidth - margin,
    81,
    { align: 'right' }
  );

  let y = 125;
  machines.forEach((machine) => {
    if (y > pageHeight - 115) {
      doc.addPage();
      y = margin;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(30, 64, 175);
    doc.text(
      `Máquina ${machine.code}${machine.name ? ` - ${machine.name}` : ''}${machine.has_open_reports ? '  (incluye reporte abierto)' : ''}`,
      margin,
      y
    );
    y += 9;

    const rows = machine.events.length > 0
      ? machine.events.map((event) => [
        event.work_order || '—',
        codeLabel(event),
        formatOptional(event.minutes),
        formatHours(event.minutes),
        formatOptional(event.quantity),
        formatOptional(event.scrap),
        event.notes || '—',
      ])
      : [[machine.has_report ? 'Sin renglones A/X' : 'Sin reporte para este turno', '', '', '', '', '', '']];
    rows.push([
      `TOTAL ${machine.code}`,
      '',
      formatNumber(machine.totals.minutes),
      formatHours(machine.totals.minutes),
      formatNumber(machine.totals.quantity),
      formatNumber(machine.totals.scrap),
      '',
    ]);

    autoTable(doc, {
      startY: y,
      head: [[`Work Order (${machine.code})`, 'Código T muerto', 'Minutos', 'Horas', 'Cantidad', 'Scrap', 'Notas']],
      body: rows,
      theme: 'grid',
      margin: { left: margin, right: margin, top: margin, bottom: 40 },
      headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: 'bold' },
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 205 },
        2: { cellWidth: 76, halign: 'right' },
        3: { cellWidth: 76, halign: 'right' },
        4: { cellWidth: 92, halign: 'right' },
        5: { cellWidth: 82, halign: 'right' },
      },
      didParseCell: (cellData) => {
        if (cellData.section === 'body' && cellData.row.index === rows.length - 1) {
          cellData.cell.styles.fillColor = [219, 234, 254];
          cellData.cell.styles.fontStyle = 'bold';
        }
      },
    });
    y = (doc.lastAutoTable?.finalY || y) + 24;
  });

  if (machines.length === 0) {
    doc.setFontSize(12);
    doc.setTextColor(100, 116, 139);
    doc.text('No hay máquinas de Conversión para este sitio.', margin, y);
  }

};

export const buildLaborAxPdf = (reports) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' });
  (Array.isArray(reports) ? reports : [reports]).forEach((report, index) => {
    if (index > 0) doc.addPage();
    appendLaborAxReport(doc, report);
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 36;
  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`Página ${page} de ${pageCount}`, pageWidth - margin, pageHeight - 18, { align: 'right' });
  }

  return doc;
};

const ConversionLaborAxPdfReport = ({ onBack }) => {
  const { currentSite } = useSite();
  const previewRef = useRef(null);
  const [date, setDate] = useState(getBusinessDateISO());
  const [shifts, setShifts] = useState([]);
  const [shiftId, setShiftId] = useState('');
  const [includeOpen, setIncludeOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    let active = true;
    setShifts([]);
    setShiftId('');
    setPdfUrl(null);
    if (currentSite?.id) {
      axios.get('/catalogs/all', { params: { site_id: currentSite.id } })
        .then(({ data }) => { if (active) setShifts(data.shifts || []); })
        .catch(() => { if (active) toast.error('No fue posible cargar los turnos'); });
    }
    return () => { active = false; };
  }, [currentSite?.id]);

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const clearPreview = () => {
    setPdfUrl(null);
  };

  const generate = async () => {
    if (!currentSite?.id || !date || !shiftId) {
      toast.error('Selecciona sitio, fecha y turno');
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.get('/conversion/reportes-pdf/mano-obra-ax', {
        params: { site_id: currentSite.id, date, shift_id: shiftId, include_open: includeOpen ? 1 : 0 },
      });
      const doc = buildLaborAxPdf(data);
      setPdfUrl(URL.createObjectURL(doc.output('blob')));
    } catch (error) {
      toast.error(error.response?.data?.errors?.shift_id?.[0] || error.response?.data?.message || 'No fue posible generar Mano de Obra AX');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 py-6">
      <div>
        <button type="button" onClick={onBack} className="mb-2 text-sm font-bold text-blue-700 hover:text-blue-900">← Todos los reportes</button>
        <h1 className="text-3xl font-extrabold text-blue-900">Mano de Obra AX</h1>
        <p className="mt-1 text-slate-600">Renglones A/X por máquina, en el orden en que fueron capturados.</p>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[190px]">
            <label htmlFor="labor-ax-date" className="mb-1 block text-sm font-bold text-slate-700">Fecha</label>
            <input id="labor-ax-date" type="date" value={date} onChange={(event) => { setDate(event.target.value); clearPreview(); }} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900" />
          </div>
          <div className="min-w-[190px]">
            <label htmlFor="labor-ax-shift" className="mb-1 block text-sm font-bold text-slate-700">Turno</label>
            <select id="labor-ax-shift" value={shiftId} onChange={(event) => { setShiftId(event.target.value); clearPreview(); }} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900">
              <option value="">Seleccionar turno...</option>
              {shifts.map((shift) => <option key={shift.id} value={shift.id}>{shift.code}{shift.name ? ` - ${shift.name}` : ''}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700">
            <input type="checkbox" checked={includeOpen} onChange={(event) => { setIncludeOpen(event.target.checked); clearPreview(); }} className="h-4 w-4" />
            Incluir reportes abiertos
          </label>
          <button type="button" onClick={generate} disabled={loading} className="rounded-lg bg-blue-700 px-5 py-2 font-bold text-white shadow hover:bg-blue-800 disabled:bg-slate-400">
            {loading ? 'Generando…' : 'Generar vista previa'}
          </button>
          {pdfUrl && (
            <>
              <button type="button" onClick={() => previewRef.current?.contentWindow?.print()} className="rounded-lg border border-blue-300 bg-white px-5 py-2 font-bold text-blue-800">Imprimir</button>
              <a href={pdfUrl} download={`mano-obra-ax-${date}-${shifts.find((shift) => String(shift.id) === String(shiftId))?.code || 'turno'}.pdf`} className="rounded-lg border border-slate-300 bg-white px-5 py-2 font-bold text-slate-800 no-underline">Descargar PDF</a>
            </>
          )}
        </div>
      </section>

      {pdfUrl ? (
        <section className="overflow-hidden rounded-2xl border border-slate-300 bg-slate-100 shadow-sm">
          <iframe ref={previewRef} src={pdfUrl} title="Vista previa de Mano de Obra AX" className="h-[75vh] min-h-[620px] w-full" />
        </section>
      ) : (
        <section className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center text-slate-500">
          Selecciona fecha y turno para generar Mano de Obra AX.
        </section>
      )}
    </div>
  );
};

export default ConversionLaborAxPdfReport;
